import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  SimpleGrid,
  List,
  ListItem,
  ListIcon,
  Button,
  Flex,
  Image,
} from "@chakra-ui/react";
import { FaCheckCircle, FaBolt, FaDollarSign, FaLeaf, FaShieldAlt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { getColor } from "../../brandConfig";

const ResidentialSolarPage = () => {
  const navigate = useNavigate();

  return (
    <>
      <NavbarWithCallToAction />

      <Box bg={getColor("background.main")} minHeight="100vh" position="relative" overflow="hidden">
        {/* Hero Section */}
        <Box
          position="relative"
          width="100vw"
          height="500px"
          overflow="hidden"
          marginLeft="50%"
          transform="translateX(-50%)"
          backgroundImage="url('/assets/onesolarimages/services/residential_solar.jpg')"
          backgroundSize="cover"
          backgroundPosition="center"
          backgroundRepeat="no-repeat"
        >
          <Box position="absolute" top={0} left={0} right={0} bottom={0} bg="rgba(0, 0, 0, 0.5)" zIndex={1} />
          <Container maxW="1200px" position="relative" zIndex={2} height="100%" display="flex" alignItems="center">
            <VStack spacing={6} align="flex-start" maxW="800px">
              <Heading
                as="h1"
                fontSize={{ base: "4xl", md: "5xl", lg: "6xl" }}
                fontWeight="bold"
                color="white"
                textShadow="2px 2px 4px rgba(0,0,0,0.5)"
                fontFamily="'Open Sans', sans-serif"
                textTransform="uppercase"
              >
                Residential Solar
              </Heading>
              <Text
                fontSize={{ base: "lg", md: "xl" }}
                color="white"
                textShadow="2px 2px 4px rgba(0,0,0,0.5)"
                fontFamily="'Open Sans', sans-serif"
              >
                Choosing solar power is one of the brightest decisions you can make for your home
              </Text>
              <Button
                size="lg"
                bg="white"
                color="#121c47"
                px={8}
                py={6}
                fontSize="md"
                fontWeight="600"
                textTransform="uppercase"
                borderRadius="4px"
                fontFamily="'Open Sans', sans-serif"
                _hover={{ bg: "gray.100", transform: "translateY(-2px)", boxShadow: "lg" }}
                transition="all 0.3s"
                onClick={() => navigate("/contact")}
              >
                Get Free Quote
              </Button>
            </VStack>
          </Container>
        </Box>

        {/* Key Benefits Banner */}
        <Box bg="#121c47" py={{ base: 8, md: 12 }} position="relative" zIndex={2}>
          <Container maxW="1200px">
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={6}>
              <Flex direction="column" align="center" textAlign="center" color="white">
                <FaDollarSign size={40} style={{ marginBottom: '1rem' }} />
                <Text fontSize="lg" fontWeight="600" fontFamily="'Open Sans', sans-serif">
                  Lower Bills
                </Text>
              </Flex>
              <Flex direction="column" align="center" textAlign="center" color="white">
                <FaBolt size={40} style={{ marginBottom: '1rem' }} />
                <Text fontSize="lg" fontWeight="600" fontFamily="'Open Sans', sans-serif">
                  Reliable Power
                </Text>
              </Flex>
              <Flex direction="column" align="center" textAlign="center" color="white">
                <FaLeaf size={40} style={{ marginBottom: '1rem' }} />
                <Text fontSize="lg" fontWeight="600" fontFamily="'Open Sans', sans-serif">
                  Eco-Friendly
                </Text>
              </Flex>
              <Flex direction="column" align="center" textAlign="center" color="white">
                <FaShieldAlt size={40} style={{ marginBottom: '1rem' }} />
                <Text fontSize="lg" fontWeight="600" fontFamily="'Open Sans', sans-serif">
                  Peace of Mind
                </Text>
              </Flex>
            </SimpleGrid>
          </Container>
        </Box>

        {/* Main Content */}
        <Box bg="white" py={{ base: 16, md: 20 }} position="relative" zIndex={2}>
          <Container maxW="1200px">
            <VStack spacing={16} align="stretch">
              {/* Introduction */}
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={12} alignItems="center">
                <Box>
                  <Box width="60px" height="2px" bg="#121c47" mb={4} />
                  <Heading
                    as="h2"
                    fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                    fontWeight="bold"
                    color="#050303"
                    fontFamily="'Open Sans', sans-serif"
                    mb={6}
                  >
                    The Most Efficient, Affordable & Reliable Solar Solutions
                  </Heading>
                  <Text fontSize={{ base: "md", md: "lg" }} color="#666" fontFamily="'Open Sans', sans-serif" lineHeight="1.8" mb={4}>
                    At One Solar New Zealand, we deliver premium residential solar solutions designed to maximize your savings and energy independence. Our experienced team ensures every installation is efficient, reliable, and tailored to your home's unique requirements.
                  </Text>
                  <Text fontSize={{ base: "md", md: "lg" }} color="#666" fontFamily="'Open Sans', sans-serif" lineHeight="1.8">
                    All our technicians are fully qualified solar designers, installers, and master electricians, ensuring your system is installed to the highest standards.
                  </Text>
                </Box>
                <Box>
                  <Image
                    src="/assets/onesolarimages/services/residential_solar.jpg"
                    alt="Residential Solar Installation"
                    width="100%"
                    height="auto"
                    borderRadius="lg"
                    objectFit="cover"
                  />
                </Box>
              </SimpleGrid>

              {/* Financial Benefits */}
              <Box bg="#f8f9fa" p={{ base: 6, md: 8 }} borderRadius="lg">
                <Heading
                  as="h3"
                  fontSize={{ base: "xl", md: "2xl" }}
                  fontWeight="bold"
                  color="#050303"
                  fontFamily="'Open Sans', sans-serif"
                  mb={4}
                  textAlign="center"
                >
                  Significant Long-Term Savings
                </Heading>
                <Text fontSize={{ base: "md", md: "lg" }} color="#666" fontFamily="'Open Sans', sans-serif" lineHeight="1.8" textAlign="center" maxW="800px" mx="auto">
                  If you're currently spending approximately $350 per quarter on your energy bill, you could <strong>save up to $50,000 over the next 25 years</strong> by switching to solar - with zero upfront costs.
                </Text>
              </Box>

              {/* Key Benefits */}
              <Box>
                <Heading
                  as="h3"
                  fontSize={{ base: "xl", md: "2xl" }}
                  fontWeight="bold"
                  color="#050303"
                  fontFamily="'Open Sans', sans-serif"
                  mb={6}
                >
                  Why Choose Residential Solar?
                </Heading>
                <List spacing={4}>
                  <ListItem>
                    <Flex align="flex-start">
                      <ListIcon as={FaCheckCircle} color="#121c47" mt={1} boxSize={5} />
                      <Box>
                        <Text fontSize={{ base: "md", md: "lg" }} fontWeight="600" color="#050303" fontFamily="'Open Sans', sans-serif">
                          Reduce Your Electricity Bills
                        </Text>
                        <Text fontSize={{ base: "md", md: "lg" }} color="#666" fontFamily="'Open Sans', sans-serif">
                          Dramatically lower your monthly power costs by generating your own clean energy
                        </Text>
                      </Box>
                    </Flex>
                  </ListItem>
                  <ListItem>
                    <Flex align="flex-start">
                      <ListIcon as={FaCheckCircle} color="#121c47" mt={1} boxSize={5} />
                      <Box>
                        <Text fontSize={{ base: "md", md: "lg" }} fontWeight="600" color="#050303" fontFamily="'Open Sans', sans-serif">
                          Earn Money from Excess Energy
                        </Text>
                        <Text fontSize={{ base: "md", md: "lg" }} color="#666" fontFamily="'Open Sans', sans-serif">
                          Sell surplus electricity back to the grid and receive credits on your power bill
                        </Text>
                      </Box>
                    </Flex>
                  </ListItem>
                  <ListItem>
                    <Flex align="flex-start">
                      <ListIcon as={FaCheckCircle} color="#121c47" mt={1} boxSize={5} />
                      <Box>
                        <Text fontSize={{ base: "md", md: "lg" }} fontWeight="600" color="#050303" fontFamily="'Open Sans', sans-serif">
                          Reduce Greenhouse Gas Emissions
                        </Text>
                        <Text fontSize={{ base: "md", md: "lg" }} color="#666" fontFamily="'Open Sans', sans-serif">
                          Make a positive environmental impact by reducing your carbon footprint
                        </Text>
                      </Box>
                    </Flex>
                  </ListItem>
                  <ListItem>
                    <Flex align="flex-start">
                      <ListIcon as={FaCheckCircle} color="#121c47" mt={1} boxSize={5} />
                      <Box>
                        <Text fontSize={{ base: "md", md: "lg" }} fontWeight="600" color="#050303" fontFamily="'Open Sans', sans-serif">
                          Increase Property Value
                        </Text>
                        <Text fontSize={{ base: "md", md: "lg" }} color="#666" fontFamily="'Open Sans', sans-serif">
                          Homes with solar systems often command higher resale values
                        </Text>
                      </Box>
                    </Flex>
                  </ListItem>
                </List>
              </Box>

              {/* How Solar Works */}
              <Box>
                <Heading
                  as="h3"
                  fontSize={{ base: "xl", md: "2xl" }}
                  fontWeight="bold"
                  color="#050303"
                  fontFamily="'Open Sans', sans-serif"
                  mb={6}
                >
                  How Residential Solar Works
                </Heading>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
                  <Box
                    border="2px solid"
                    borderColor="#121c47"
                    p={6}
                    borderRadius="lg"
                  >
                    <Heading as="h4" fontSize="lg" fontWeight="600" color="#121c47" fontFamily="'Open Sans', sans-serif" mb={3}>
                      Grid-Connected Systems
                    </Heading>
                    <Text fontSize="md" color="#666" fontFamily="'Open Sans', sans-serif" lineHeight="1.8">
                      The most common residential solar system in New Zealand. Stay connected to the grid for nighttime and cloudy day power needs, while selling excess solar power back to your electricity provider for credits.
                    </Text>
                  </Box>
                  <Box
                    border="2px solid"
                    borderColor="#121c47"
                    p={6}
                    borderRadius="lg"
                  >
                    <Heading as="h4" fontSize="lg" fontWeight="600" color="#121c47" fontFamily="'Open Sans', sans-serif" mb={3}>
                      Off-Grid Systems
                    </Heading>
                    <Text fontSize="md" color="#666" fontFamily="'Open Sans', sans-serif" lineHeight="1.8">
                      Perfect for remote properties or new builds where grid connection is expensive. Solar panels combined with battery storage provide 24-hour power without any grid connection required.
                    </Text>
                  </Box>
                </SimpleGrid>
              </Box>

              {/* CTA Section */}
              <Box
                bg="#121c47"
                color="white"
                p={{ base: 8, md: 12 }}
                borderRadius="lg"
                textAlign="center"
              >
                <Heading
                  as="h3"
                  fontSize={{ base: "xl", md: "2xl", lg: "3xl" }}
                  fontWeight="bold"
                  fontFamily="'Open Sans', sans-serif"
                  mb={4}
                >
                  Ready to Save on Your Power Bills?
                </Heading>
                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  fontFamily="'Open Sans', sans-serif"
                  mb={6}
                  maxW="700px"
                  mx="auto"
                >
                  Contact us today for a free consultation. Our expert team will assess your home and provide a customized solar solution that maximizes your savings.
                </Text>
                <Button
                  size="lg"
                  bg="white"
                  color="#121c47"
                  px={8}
                  py={6}
                  fontSize="md"
                  fontWeight="600"
                  textTransform="uppercase"
                  borderRadius="4px"
                  fontFamily="'Open Sans', sans-serif"
                  _hover={{ bg: "gray.100", transform: "translateY(-2px)", boxShadow: "lg" }}
                  transition="all 0.3s"
                  onClick={() => navigate("/contact")}
                >
                  Get Your Free Quote
                </Button>
              </Box>
            </VStack>
          </Container>
        </Box>

        <FooterWithFourColumns />
      </Box>
    </>
  );
};

export default ResidentialSolarPage;
